/** Authentification par lien magique. */

import { initialHash, supabase } from './client.js'

/**
 * Envoie le lien de connexion.
 *
 * shouldCreateUser reste à true : Supabase crée le compte à la première
 * connexion, ce qui évite de pré-créer les utilisateurs à la main. Ce n'est pas
 * une faille — avoir un compte ne donne accès à rien. L'accès aux données tient
 * entièrement aux politiques RLS adossées à allowed_emails (supabase/schema.sql).
 * Un inconnu qui demande un lien obtient donc une session parfaitement vide.
 */
export async function sendMagicLink(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: {
      shouldCreateUser: true,
      emailRedirectTo: window.location.origin + window.location.pathname,
    },
  })
  if (error) throw error
}

export async function signOut() {
  await supabase.auth.signOut()
}

export async function getSession() {
  const { data } = await supabase.auth.getSession()
  return data.session
}

/** Notifie à chaque changement de session (connexion, déconnexion, jeton rafraîchi). */
export function onAuthChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => callback(session))
  return () => data.subscription.unsubscribe()
}

/**
 * Vérifie que l'utilisateur connecté est sur la liste blanche.
 *
 * Distingue deux situations que RLS rend autrement indiscernables : un compte
 * non autorisé ne voit rien, exactement comme une base vide. Sans cette
 * vérification, quelqu'un d'oublié dans allowed_emails croirait à un outil
 * cassé plutôt qu'à un défaut d'autorisation.
 */
export async function isAllowed() {
  const { data, error } = await supabase.from('allowed_emails').select('email').limit(1)
  if (error) return false
  return data.length > 0
}

/**
 * Messages des échecs de lien magique.
 *
 * Les libellés de Supabase sont en anglais et techniques (« Email link is
 * invalid or has expired ») : ils décrivent le symptôme sans dire quoi faire.
 */
const AUTH_ERRORS = {
  otp_expired:
    'Ce lien a expiré ou a déjà servi — un lien de connexion ne fonctionne qu\'une fois. ' +
    'Demandes-en un nouveau ci-dessous.',
  access_denied: 'Connexion refusée. Demande un nouveau lien ci-dessous.',
}

/**
 * Lit le retour du lien magique et nettoie l'URL au passage.
 *
 * Deux fragments possibles : le jeton en cas de succès (#access_token=...), ou
 * l'erreur en cas d'échec (#error=...&error_code=...). Les deux doivent
 * disparaître de la barre d'adresse — le jeton parce qu'un copier-coller de
 * l'URL le transmettrait, l'erreur parce qu'elle survivrait à la reconnexion
 * et ferait croire à une panne persistante.
 *
 * @returns {{error: string | null}} message prêt à afficher
 */
export function consumeAuthFragment() {
  // initialHash, et non window.location.hash : Supabase a déjà effacé ce
  // dernier au moment où l'on arrive ici. Voir client.js.
  const hash = initialHash
  if (!hash || hash.length < 2) return { error: null }

  const params = new URLSearchParams(hash.slice(1))
  const code = params.get('error_code')
  const raw = params.get('error')

  const hasToken = params.has('access_token')
  const hasError = Boolean(code || raw)
  if (!hasToken && !hasError) return { error: null }

  // Souvent déjà fait par Supabase ; on le refait sans condition, car il ne
  // nettoie pas tous les cas (le paramètre `sb=` d'un lien rejoué, notamment).
  if (window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
  }

  if (!hasError) return { error: null }
  return {
    error:
      AUTH_ERRORS[code] ||
      AUTH_ERRORS[raw] ||
      params.get('error_description')?.replace(/\+/g, ' ') ||
      'La connexion a échoué. Demande un nouveau lien ci-dessous.',
  }
}
